import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { TrendingUp, AlertTriangle } from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const formatDay = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const tooltipStyle = {
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1)",
};

export default function TrendsChart({ trends }) {
  const daily = (trends?.daily || []).map((d) => ({ ...d, label: formatDay(d.date) }));
  const errorsByCategory = trends?.errorsByCategory || [];

  return (
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm lg:col-span-2">
          <CardHeader className="border-b border-gray-100/80 bg-gray-50/50 pb-4">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </span>
              Requests & Errors ({trends?.days || 14}-day trend)
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Daily volume, not just the current total
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            {daily.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ paddingTop: 8 }} />
                    <Line type="monotone" dataKey="requests" stroke="#3b82f6" name="Requests" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="errors" stroke="#ef4444" name="Errors" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-64 flex-col items-center justify-center text-gray-500">
                <TrendingUp className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm">No trend data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm">
          <CardHeader className="border-b border-gray-100/80 bg-gray-50/50 pb-4">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </span>
              Errors by Module
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Which area is producing the most errors
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            {errorsByCategory.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={errorsByCategory}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="category" width={90} tick={{ fill: "#64748b", fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#ef4444" name="Errors" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-64 flex-col items-center justify-center text-gray-500">
                <AlertTriangle className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm">No errors in this window</p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
  );
}
